<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card class="q-dialog-plugin">
      <q-form @submit.prevent="update">
        <q-card-section>
          <div class="text-h6">Обновить выбранные задачи</div>
        </q-card-section>
        <q-card-section>
          <q-select use-chips multiple v-model="taskLabels" clearable :loading="isLabelLoading" :options="labels"
                    option-label="name" option-value="id" label="Ярлыки" emit-value behavior="menu" map-options>
            <template v-slot:no-option>
              <q-item>
                <q-item-section class="text-italic text-grey">
                  Нет доступных ярлыков
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-select use-chips multiple v-model="performers" clearable :loading="isUserLoading"
                    :options="performerUsers" :option-label="(user) => `${user.username} (${user.email})`"
                    option-value="email" label="Исполнители" emit-value behavior="menu" map-options/>
          <q-input
              v-model.number="photoCount"
              type="number"
              label="Кол-во фотографий"
          />
          <q-input
              v-model.trim="description"
              type="textarea"
              label="Описание"
          />
          <q-input v-model="startTime" label="Дата начала">
            <template v-slot:prepend>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-date v-model="startTime" mask="YYYY-MM-DD HH:mm">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Закрыть" color="primary" flat/>
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>

            <template v-slot:append>
              <q-icon name="access_time" class="cursor-pointer">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-time v-model="startTime" mask="YYYY-MM-DD HH:mm" format24h>
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Закрыть" color="primary" flat/>
                    </div>
                  </q-time>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
          <q-input v-model="executionTime" label="Дата выполнения">
            <template v-slot:prepend>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-date v-model="executionTime" mask="YYYY-MM-DD HH:mm">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Закрыть" color="primary" flat/>
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>

            <template v-slot:append>
              <q-icon name="access_time" class="cursor-pointer">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-time v-model="executionTime" mask="YYYY-MM-DD HH:mm" format24h>
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Закрыть" color="primary" flat/>
                    </div>
                  </q-time>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn :loading="copied" no-caps label="Обновить" type="submit" color="primary"/>
          <q-btn :disable="copied" color="primary" label="Отмена" no-caps @click="onDialogCancel"/>
        </q-card-actions>
      </q-form>

    </q-card>
  </q-dialog>
</template>
<script setup lang="ts">
import {useDialogPluginComponent, useQuasar} from 'quasar'
import {useProjectLabels, useProjectUsers, useSelectProjects} from '../composables/project'
import {
  useAddTask,
  useAddTaskLabels,
  useAddTaskPerformers,
  useAddTasksLabels,
  useAddTasksPerformers,
  useTask,
  useTaskTypes
} from '../composables/task'
import {computed, ref, toRefs, watch} from 'vue'
import {requiredRule, notEmptyRule} from "../util/rules";
import {NewTask} from '../models/task';
import {useDayJs} from '../composables/dayjs';
import {useRouteParams} from '@vueuse/router';
import {useRouter} from 'vue-router';
import {usePerformTask, useUpdatePerformTask, useUpdatePerformTasks} from '../composables/performTask';
import {UpdatePerformTask} from '../models/performTask';
import {useEventBus} from '@vueuse/core';
import {taskKey} from '../util/events';

const dayjs = useDayJs()
const projectId = useRouteParams('projectId', undefined, {transform: Number})
const taskEvent = useEventBus(taskKey)
const $q = useQuasar()
const props = withDefaults(defineProps<{
  tasks: number[],
}>(), {tasks: []})

const {tasks} = toRefs(props)
defineEmits([
  // REQUIRED; need to specify some events that your
  // component will emit through useDialogPluginComponent()
  ...useDialogPluginComponent.emits
])

const selectedProject = ref(null)
const {data: projects, isFetching: isProjectLoading} = useSelectProjects()

const {dialogRef, onDialogHide, onDialogOK, onDialogCancel} = useDialogPluginComponent()
const {data: labels, isFetching: isLabelLoading} = useProjectLabels(projectId)
const {data: users, isFetching: isUserLoading} = useProjectUsers(projectId)
const {data: taskTypes, isFetching: isTaskTypeLoading} = useTaskTypes(projectId)

const performerUsers = computed(() => users.value?.filter(u => u.permission !== "READ").map(u => u.user) ?? [])
const selectedType = computed(() => !!taskType.value ? taskTypes.value?.find(t => t.id === taskType.value) : null)
const taskLabels = ref<number[]>([])
const performers = ref<string[]>([])
const executionTime = ref(null)
const photoCount = ref<number | null>(null)
const startTime = ref(null)
const description = ref('')
const taskType = ref(null)
const copyTasks = ref(0)
const copied = ref(false)
const updateBody = computed(() => ({
  executionTime: executionTime.value ? dayjs(executionTime.value).utc().format() : null,
  startTime: startTime.value ? dayjs(startTime.value).utc().format() : null,
  photoCount: photoCount.value ? photoCount.value : null,
  description: description.value ? description.value : null
}))

const {execute: executeUpdateLabels} = useAddTasksLabels(projectId, tasks, taskLabels)
const {execute: executeUpdatePerformers} = useAddTasksPerformers(projectId, tasks, performers)
const {execute: executeUpdatePerformTask} = useUpdatePerformTasks(projectId, tasks, updateBody)

const router = useRouter()
const properties = ref([])
watch(selectedType, val => {
  properties.value = val?.propertyDescription.map(d => ({
    ...d,
    value: null
  })) ?? []
})

async function update() {
  copied.value = true
  try {
    if (tasks.value.length > 0) {
      if (taskLabels.value.length != 0) {
        await executeUpdateLabels(true)
      }
      if (performers.value.length != 0) {
        await executeUpdatePerformers(true)
      }
    }
    if (executionTime.value || startTime.value || photoCount.value || description.value) {
      await executeUpdatePerformTask(true)
    }
  } catch (e) {
  }
  taskEvent.emit('refresh')
  copied.value = false
  onDialogOK()
}
</script>
